import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Drinks.css";

interface DrinkItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Drinks: React.FC = () => {
  const [data, setData] = useState<DrinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/drinks`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching drinks:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: DrinkItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: DrinkItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Drinks",
    de: "Getränke",
    ta: "பானங்கள்",
    si: "පාන",
    hi: "पेय",
    ar: "مشروبات",
    tr: "İçecekler",
    ru: "Напитки",
    zh: "饮料",
    fr: "Boissons",
    es: "Bebidas",
  };

  const subtitleMap = {
    en: "Drinks in German",
    de: "Getränke auf Deutsch",
    ta: "ஜெர்மன் மொழியில் பானங்கள்",
    si: "ජර්මන් භාෂාවෙන් පාන",
    hi: "जर्मन में पेय",
    ar: "المشروبات بالألمانية",
    tr: "Almanca'da İçecekler",
    ru: "Напитки на немецком",
    zh: "德语饮料",
    fr: "Boissons en allemand",
    es: "Bebidas en alemán",
  };

  return (
    <div className="drinks-page-container">
      <div className="drinks-page-header">
        <div>
          <h1 className="drinks-page-title">{titleMap["de"]}</h1>
          <p className="drinks-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="drinks-page-loading">Loading...</div>
      ) : (
        <div className="drinks-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`drinks-page-card ${
                  isPlaying ? "drinks-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="drinks-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="drinks-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="drinks-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="drinks-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="drinks-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Drinks;
